import { createSlice, 
         PayloadAction 
       }                        from "@reduxjs/toolkit";
import { RootState }            from "store/Store";
import { GetApplianceProfile, 
         GetApplianceProfileMetadata,
         UpdateApplianceProfile 
       }                        from "types/apis/applianceProfileInterfaces";
import { APIData }              from "types/cms/frontendTypes";

export interface ApplianceProfileReducerState {
  categories  : APIData<'api::appliance-category.appliance-category'>[]
  appliances  : APIData<'api::appliance.appliance'>[]
  profile     : APIData<'api::utility-appliance-profile.utility-appliance-profile'> | undefined
  applianceOwnership : Record<string, Record<string, Record<string, string[]>>>
  unsavedRows: Record<string, any>;
  hasUnsavedRows: boolean;
}

const defaultState : ApplianceProfileReducerState = {
  categories : [],
  appliances : [],
  profile : undefined,
  applianceOwnership : {},
  unsavedRows: {},
  hasUnsavedRows: false
}

export const applianceProfileSlice = createSlice({
  name : 'applianceProfile',
  initialState : defaultState,
  reducers : {

    setApplianceProfileMetadata : (state : ApplianceProfileReducerState, 
                                  action : PayloadAction<GetApplianceProfileMetadata.retval>) => {
      state.categories = action.payload.categories
      state.appliances = action.payload.appliances.map((app : APIData<'api::appliance.appliance'>) => {
        return {
          ...app,
          applianceId : app.applianceId.toString()
        }
      }) as any as APIData<'api::appliance.appliance'>[]
    },

    setApplianceProfile : (state : ApplianceProfileReducerState, 
                           action : PayloadAction<GetApplianceProfile.retval>) => {

      state.profile = action.payload
      recomputeApplianceOwnership(state)
    },

    updateApplianceProfile : (state : ApplianceProfileReducerState, 
                             action : PayloadAction<UpdateApplianceProfile.retval>) => {
                              
      state.profile = action.payload
      recomputeApplianceOwnership(state)
    },

    setUnsavedRowAction: (state: ApplianceProfileReducerState, action: PayloadAction<{
      id: string;
      diff: any;
    }>) => {
      state.unsavedRows[action.payload.id] = action.payload.diff;
      state.hasUnsavedRows = true;
    },

    discardUnsavedRowAction: (state: ApplianceProfileReducerState, action: PayloadAction<string>) => {
      delete state.unsavedRows[action.payload];
      if (Object.keys(state.unsavedRows).length === 0) {
        state.hasUnsavedRows = false;
      }
    },

    discardAllUnsavedRowsAction: (state: ApplianceProfileReducerState) => {
      state.unsavedRows = {};
      state.hasUnsavedRows = false;
    }
  } as any
}) as any

const recomputeApplianceOwnership = (state : ApplianceProfileReducerState) => {

  const appOwnership = {} as any

  const disabledAppliances = (state.profile?.disabled || []) as string[]
  const categoryOverrides = (state.profile?.applianceCategoryOverrides || {}) as Record<string, {category : string, subCategory : string}>

  for (const appliance of state.appliances) {

    if (disabledAppliances.includes(appliance.applianceId.toString())) {
      continue
    }

    let categoryId = appliance.categoryId
    let subCategoryId = appliance.subCategoryId

    if (categoryOverrides[appliance.applianceId.toString()]) {
      const { category, subCategory } = categoryOverrides[appliance.applianceId.toString()]
      categoryId = category
      subCategoryId = subCategory
    } 
    
    const categoryObj = state.categories.find(c => c.categoryId === categoryId)!
    const categoryName = categoryObj.name
    const filterOnAppCount = categoryObj.applianceCountFilter 
    const trueDisagg = categoryObj.trueDisagg

    const subCategoryObj = (categoryObj.subCategory as any[]).find(sc => sc.id === subCategoryId)
    const subCategoryName = subCategoryObj.name
    const filterOnApplianceLevel = subCategoryObj.applianceLevelFilter
    
    const fuelTypes = appliance.fuelType
    const applianceId = appliance.applianceId
    const applianceName = appliance.name
    
    if (!appOwnership[categoryId]) {
      appOwnership[categoryId] = {
        categoryName,
        filterOnAppCount,
        trueDisagg,
        subCategories : []
      }
    }

    if (!appOwnership[categoryId].subCategories.find(
        (sc : any) => sc.subCategoryId === subCategoryId)) {

      appOwnership[categoryId].subCategories.push({
        subCategoryId,
        subCategoryName,
        filterOnApplianceLevel,
        applianceFilters : []
      })    
    }

    appOwnership[categoryId].subCategories.find(
      (sc : any) => sc.subCategoryId === subCategoryId)!.applianceFilters.push({
      applianceId : applianceId.toString(),
      applianceName,
      fuelTypes
    })
  }

  state.applianceOwnership = appOwnership
} 

export const {
  setApplianceProfileMetadata, 
  setApplianceProfile,
  updateApplianceProfile,
  setUnsavedRowAction,
  discardUnsavedRowAction,
  discardAllUnsavedRowsAction
} = applianceProfileSlice.actions

export const selectCategories = (state : RootState) => (state.applianceProfile as ApplianceProfileReducerState).categories
export const selectAppliances = (state : RootState) => (state.applianceProfile as ApplianceProfileReducerState).appliances  
export const selectProfile = (state : RootState) => (state.applianceProfile as ApplianceProfileReducerState).profile
export const selectApplianceOwnership = (state : RootState) => (state.applianceProfile as ApplianceProfileReducerState).applianceOwnership
export const selectUnsavedRows = (state: RootState) => (state.applianceProfile as ApplianceProfileReducerState).unsavedRows;
export const selectHasUnsavedRows = (state: RootState) => (state.applianceProfile as ApplianceProfileReducerState).hasUnsavedRows;